// THE WORLD QUERY — the one service that answers the two questions the world
// could not answer before:
//
//   1. ray(origin, dir, maxDist)            what does this ray hit?
//   2. segmentBlocked(ax, az, bx, bz, y0, y1, opts)  is A→B blocked?
//
// Fifteen separately-reported defects were one missing answer. Hitscan picked
// against mesh NAMES ('ground' / 'crate' / 'dummy*') that the 10 km map never
// creates, every world mesh is isPickable = false, so there was no cover
// anywhere on the map: bullets went through terrain, walls, doors and trees,
// the impact decal / impact sound / ricochet were unreachable, tracers always
// died at origin + dir * 80, gun PvP and animals never resolved, melee was four
// disagreeing 2D arc copies with no Y band and no occlusion, the infected swing
// had no occlusion at all, infected line-of-sight sampled a FIXED FOUR points
// over the whole ray (15 m granularity at 60 m), crowd separation squeezed
// bodies through walls, and the wolves had no obstacle model at all.
//
// WHY ANALYTIC AND NOT MESH PICKING: making the world's meshes pickable would
// hang a per-frame octree/AABB cost on a 10x10 km map for an answer needed a
// few times a second, and it cannot run on the server at all.
//
// So this module walks the colliders that ALREADY EXIST and are already trusted
// for player movement — nothing new is generated:
//   • buildings.js  this.hash          per-wall OBBs with y intervals, door
//                                      holes already cut out of the segments
//   • doors.js      this.hash          one OBB per opening, solid only while shut
//   • props.js      this.colHash       prop/wreck/fence OBBs with y intervals
//   • vegetation.js this.trunkHash     trunk circles with a bole y interval
//   • terrain.js    getHeight(x, z)    the height field that IS the surface
//
// DEPENDENCY-FREE AND PURE, for the same three reasons src/world/infected-sim.mjs
// is: it must be unit-testable in plain Node (tools/querytest.mjs is the only
// proof this work is correct — there is no browser in the loop), the server's
// infected and wolf sims need the same occlusion answers as the client, and one
// copy of the mathematics is the only way the two can agree. It is .mjs on
// purpose: package.json has no "type": "module", so a .js file here would load
// as CommonJS under Node and the server could not import it.
//
// Everything arrives by INJECTION through the provider object handed to
// createQuery(): in the browser the closures read the hashes above, in a test
// they read hand-built fixtures or real towns.json geometry.
//
// ---------------------------------------------------------------------------
// WHAT THIS DELIBERATELY DOES NOT DO
//   • It does not own, build, cache or invalidate colliders. It reads what the
//     providers hand it, every call, so an opened door or a moved body is live
//     with no bookkeeping.
//   • It does not resolve gameplay. It reports geometry; damage, decals, sounds,
//     hit-once-per-swing and combo rules stay in the callers.
//   • segmentBlocked() is deliberately 2D + a y band: it consults walls, doors,
//     props, trunks and bodies but NOT the terrain, because it exists for melee
//     occlusion and AI line-of-sight at a few metres to 60 m, where a vertical
//     band through the collider set is the honest model and a terrain march is
//     not affordable per AI tick. A caller that needs terrain occlusion (a
//     sniper's sight line over a ridge) wants ray() between the two eye points.
//   • It does not do swept volumes. Both queries are infinitely thin lines; a
//     body radius belongs in the caller's reach/tolerance numbers.
//   • It is not a movement solver. pushOut() in buildings/props/doors/vegetation
//     stays where it is; this module never moves anything.
//
// CONVENTIONS
//   • Metres, right-handed, y UP. Angles in radians.
//   • Points and directions are {x, y, z} — a Babylon Vector3 satisfies that,
//     so the browser passes its own vectors straight in, and the returned
//     point/normal are plain objects the caller can feed to new Vector3(...).
//   • dir need not be normalised; it is normalised internally, so `dist` and
//     `maxDist` are always metres along the ray.
//   • A y interval is [y0, y1] with y0 <= y1; either order is accepted and
//     normalised. Missing bounds mean unbounded.
//   • A collider's local frame is the SAME one buildings.js/props.js/doors.js
//     use in pushOut(): lx = px*cos + pz*sin, lz = -px*sin + pz*cos. Feeding
//     their stored tuples in unchanged therefore cannot disagree with movement.
//
// MEASURED COST — tools/querytest.mjs, real height field + real towns.json
// footprints, on the project box (loadavg ~95 while measuring, so the wall
// clocks are pessimistic and the SAMPLE COUNTS are the real contract):
//   • ray(), 300 m fired from inside real settlements: 79 us, 213 height
//     samples, 41 slab tests, 2.0 provider lookups per ray. The average is far
//     under the worst case because 85 % of shots hit something: the corridor is
//     walked in ORDERED chunks and abandoned as soon as the best hit lies
//     inside the chunk just finished, so a wall at 5 m costs one chunk.
//     Budget it per SHOT — a rifle on auto is 9 shots/s, so ~1.4 ms of CPU per
//     second of sustained fire — never per frame per entity.
//   • ray(), 300 m grazing over open country from eye height: 371 height
//     samples on average, 1067 worst observed. This is the dear case and it is
//     dear on purpose: a nearly-horizontal ray 1.7 m over the ground is close
//     to the surface for its whole length, so the march pays four corner
//     samples per terrain cell for a slope bound it can trust (see
//     marchTerrain (b)). It was 113 samples before this was correct, and those
//     113 both MISSED hits and overshot them by up to 160 m.
//   • ray() climbing away from the ground (a shot at the sky): 88 samples —
//     the clearance grows, the free global bound takes over from the local one
//     within ~20 m, and the march strides out at coarseStep.
//   • segmentBlocked(): no height samples at all, ever. 60 m across a dense
//     town is 22 us; 2.4 m of melee reach is 7.9 us and a handful of slab
//     tests. 150 infected at 5 Hz asking once each is 750 calls/s — about 2 %
//     of a core. Still not free: never per frame per entity pair.
//   • That 22 us is dominated by buildings.js hashing a whole sealed building
//     under its CENTRE cell only, which forces a ~55 m lookup radius. Measured:
//     registering each OBB in every 16 m cell its bounding box touches — what
//     buildings.floorHash and server/zombies.mjs Obstacles already do — gives
//     the same answers at 5.8 us, a 4x win, and would let maxExtent drop to 0
//     the way server/wolves.mjs already does. See the handoff note.
// ---------------------------------------------------------------------------

// Surface tags. The impact effect and the footstep/noise classifier both
// consume these, so they are ONE vocabulary: the ground tags are exactly the
// ones main.js's world.surfaceAt() already returns and src/game/noise.js's
// SURFACE table already weighs, extended with the hard-surface and flesh tags
// an impact needs. A tag outside this set is passed through untouched (a caller
// may tag its own colliders) — these are the ones the built-in defaults use.
export const SURFACES = [
  'grass', 'forest', 'sand', 'snow', 'dirt', 'rock', 'asphalt', 'water',
  'concrete', 'wood', 'metal', 'glass', 'flesh',
];

// Hit zones, exactly the three strings infected-sim.mjs applyDamage() already
// switches on — so a ray hit feeds straight into it with no translation.
export const ZONES = ['head', 'body', 'legs'];

const EPS = 1e-9;

// This map's master height grid (map10k.json master.cellSize). The march steps
// at HALF a cell by default; see the non-tunnelling argument on marchTerrain().
const DEFAULT_CELL = 2.44140625;

// A true Lipschitz bound on |dh/dxz| for the shipped height field: the measured
// maximum over all 1024 LOD0 tiles of the generated world is 47.19 (a 115 m
// step across one 2.44 m cell, in the alpine cliffs). 94.3 % of cells are under
// 1.0 and 99.8 % under 4.0, so this number is a worst case that exists in a
// handful of places and is used ONLY to decide when a step may be lengthened —
// never to decide that a cell may be skipped.
const DEFAULT_SLOPE_BOUND = 48;

// ---------------------------------------------------------------------------
// Spatial-hash helper, exported because all four browser providers need the
// same six lines and a wrong radius here is a silent correctness bug. Gathers
// every entry of a `Map` keyed `${floor(x/cell)}_${floor(z/cell)}` whose cell
// can hold a collider whose CENTRE lies within r of (x, z).
//
// Pass `out` to reuse a scratch array: the query consumes what a provider
// returns before it calls that provider again, so one buffer per closure is
// safe and keeps a ray allocation-free.
export function gatherCells(hash, cell, x, z, r, out) {
  const res = out || [];
  res.length = 0;
  if (!hash) return res;
  const x0 = Math.floor((x - r) / cell), x1 = Math.floor((x + r) / cell);
  const z0 = Math.floor((z - r) / cell), z1 = Math.floor((z + r) / cell);
  for (let cz = z0; cz <= z1; cz++) {
    for (let cx = x0; cx <= x1; cx++) {
      const list = hash.get(`${cx}_${cz}`);
      if (!list) continue;
      for (let i = 0; i < list.length; i++) res.push(list[i]);
    }
  }
  return res;
}

// ---------------------------------------------------------------------------
// Record readers. Every source may hand back either the raw tuple the existing
// hashes already store — zero conversion, zero allocation on the hot path — or
// an object that additionally carries a `surface` / `entity` of its own.
//
//   box    [x, z, hw, hd, cos, sin, y0, y1]   buildings.hash, props.colHash
//          or { x, z, hw, hd, cos, sin, y0, y1, yaw, surface, entity }
//   trunk  [x, z, r]  /  [x, z, r, y0, y1]    vegetation.trunkHash
//          or { x, z, r, y0, y1, surface, entity }
//   door   a doors.js door object: { cx, cz, tx, tz, half, floorY, height,
//          angle, open, type } — or any object carrying an explicit `solid`.
//   body   { x, z, r, y0, y1, headY0, legsY1, entity, surface }
// ---------------------------------------------------------------------------

// Doors mirror Doors._solid(): a leaf swung more than ~0.4 rad is a hole you
// can shoot and walk through, exactly as the movement collider already treats
// it. `solid` on the record wins, so a server-side provider with no swing
// animation can say so directly.
function doorSolid(d) {
  if (typeof d.solid === 'boolean') return d.solid;
  if (typeof d.angle === 'number') return Math.abs(d.angle) < 0.4;
  return !d.open;
}

// Door thickness. doors.js collides a shut leaf as a 0.09 m half-depth slab
// (pushOut) and probes it at 0.2 m (blockedBand); a ray wants the leaf itself,
// so 0.09 m is the honest half-depth.
const DOOR_HALF_T = 0.09;

// Floor under a shrunk half-extent (see the `shrink` field on a source). A
// collider must never be shrunk to nothing: a zero-width box intersects
// nothing and the cover would disappear instead of becoming accurate.
const MIN_HALF = 0.02;

// ---------------------------------------------------------------------------
// Primitive intersections. All of them write into a caller-owned `hit` object
// and return true/false, so a ray never allocates per candidate.
// ---------------------------------------------------------------------------

// Ray vs oriented box (3 slabs: local x, local z, world y).
//
// Starting INSIDE a solid box is a real case — a muzzle clipped into a wall, an
// infected whose home post is embedded in geometry (73 of 709 are). It reports
// a hit at dist 0 with the normal facing back along the ray, because for every
// consumer the useful answer is "blocked here", not "no hit".
function rayBox(ox, oy, oz, dx, dy, dz, maxDist,
  bx, bz, hw, hd, cos, sin, by0, by1, hit) {
  const px = ox - bx, pz = oz - bz;
  const lox = px * cos + pz * sin;        // the pushOut() frame, unchanged
  const loz = -px * sin + pz * cos;
  const ldx = dx * cos + dz * sin;
  const ldz = -dx * sin + dz * cos;

  if (lox > -hw && lox < hw && loz > -hd && loz < hd && oy > by0 && oy < by1) {
    hit.dist = 0;
    hit.nx = -dx; hit.ny = -dy; hit.nz = -dz;
    hit.inside = true;
    // Where this box ENDS along the ray. A caller that knows an origin inside
    // a collider is an artefact rather than a buried muzzle (controller.js
    // does, for the movement pads around props) needs somewhere to re-fire
    // from, and the only honest place is past the far face.
    let ex = Infinity;
    if (ldx > EPS) ex = (hw - lox) / ldx; else if (ldx < -EPS) ex = (-hw - lox) / ldx;
    if (ldz > EPS) { const e = (hd - loz) / ldz; if (e < ex) ex = e; } else if (ldz < -EPS) { const e = (-hd - loz) / ldz; if (e < ex) ex = e; }
    if (dy > EPS) { const e = (by1 - oy) / dy; if (e < ex) ex = e; } else if (dy < -EPS) { const e = (by0 - oy) / dy; if (e < ex) ex = e; }
    hit.exit = ex;
    return true;
  }

  let tmin = -Infinity, tmax = maxDist, axis = 0, neg = false;
  // local x slab
  if (ldx > -EPS && ldx < EPS) { if (lox < -hw || lox > hw) return false; } else {
    const inv = 1 / ldx;
    let t1 = (-hw - lox) * inv, t2 = (hw - lox) * inv;
    let lowIsNeg = true;                   // entering through the -X face
    if (t1 > t2) { const s = t1; t1 = t2; t2 = s; lowIsNeg = false; }
    if (t1 > tmin) { tmin = t1; axis = 0; neg = lowIsNeg; }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  // local z slab
  if (ldz > -EPS && ldz < EPS) { if (loz < -hd || loz > hd) return false; } else {
    const inv = 1 / ldz;
    let t1 = (-hd - loz) * inv, t2 = (hd - loz) * inv;
    let lowIsNeg = true;
    if (t1 > t2) { const s = t1; t1 = t2; t2 = s; lowIsNeg = false; }
    if (t1 > tmin) { tmin = t1; axis = 1; neg = lowIsNeg; }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  // world y slab
  if (dy > -EPS && dy < EPS) { if (oy < by0 || oy > by1) return false; } else {
    const inv = 1 / dy;
    let t1 = (by0 - oy) * inv, t2 = (by1 - oy) * inv;
    let lowIsNeg = true;
    if (t1 > t2) { const s = t1; t1 = t2; t2 = s; lowIsNeg = false; }
    if (t1 > tmin) { tmin = t1; axis = 2; neg = lowIsNeg; }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  // Behind the origin, or past the end of the ray. (A NaN anywhere in the
  // collider record lands here: every comparison against NaN is false, so the
  // slab test rejects it instead of returning a poisoned hit.)
  if (!(tmin >= 0) || tmin > maxDist) return false;

  hit.dist = tmin;
  hit.inside = false;
  hit.exit = 0;
  // Local +X is world (cos, 0, sin); local +Z is world (-sin, 0, cos) — the
  // inverse of the rotation above.
  const sg = neg ? -1 : 1;
  if (axis === 0) {
    hit.nx = cos * sg; hit.ny = 0; hit.nz = sin * sg;
  } else if (axis === 1) {
    hit.nx = -sin * sg; hit.ny = 0; hit.nz = cos * sg;
  } else {
    hit.nx = 0; hit.ny = sg; hit.nz = 0;
  }
  return true;
}

// Ray vs vertical cylinder — trunks and bodies. An absent band still means
// unbounded, because a provider is allowed to hand over bare circles — but
// vegetation.js now publishes a real bole band (BOLE_CONIF / BOLE_DECID), so
// a level shot fired 12 m above the base of a 6 m birch no longer dies in
// mid-air against an infinite column of wood.
function rayCylinder(ox, oy, oz, dx, dy, dz, maxDist,
  cx, cz, r, cy0, cy1, hit) {
  const ex = ox - cx, ez = oz - cz;
  const inside2D = ex * ex + ez * ez < r * r;
  if (inside2D && oy > cy0 && oy < cy1) {
    hit.dist = 0;
    hit.nx = -dx; hit.ny = -dy; hit.nz = -dz;
    hit.inside = true;
    // the exit, for the same reason rayBox reports one
    let out = Infinity;
    const a0 = dx * dx + dz * dz;
    if (a0 > EPS) {
      const b0 = 2 * (ex * dx + ez * dz);
      const d0 = b0 * b0 - 4 * a0 * (ex * ex + ez * ez - r * r);
      if (d0 >= 0) out = (-b0 + Math.sqrt(d0)) / (2 * a0);
    }
    if (dy > EPS) { const e = (cy1 - oy) / dy; if (e < out) out = e; } else if (dy < -EPS) { const e = (cy0 - oy) / dy; if (e < out) out = e; }
    hit.exit = out;
    return true;
  }
  // the y band, as a slab
  let tmin = -Infinity, tmax = maxDist, capNeg = false, onCap = false;
  if (dy > -EPS && dy < EPS) { if (oy < cy0 || oy > cy1) return false; } else {
    const inv = 1 / dy;
    let t1 = (cy0 - oy) * inv, t2 = (cy1 - oy) * inv;
    let lowIsNeg = true;
    if (t1 > t2) { const s = t1; t1 = t2; t2 = s; lowIsNeg = false; }
    if (t1 > tmin) { tmin = t1; capNeg = lowIsNeg; onCap = true; }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  // the circle, as a quadratic in the horizontal plane
  const a = dx * dx + dz * dz;
  if (a < EPS) {
    // straight up or down: inside the circle or nothing
    if (!inside2D) return false;
  } else {
    const b = 2 * (ex * dx + ez * dz);
    const c = ex * ex + ez * ez - r * r;
    const disc = b * b - 4 * a * c;
    if (!(disc >= 0)) return false;
    const sq = Math.sqrt(disc);
    const inv2a = 1 / (2 * a);
    const t1 = (-b - sq) * inv2a, t2 = (-b + sq) * inv2a;
    if (t1 > tmin) { tmin = t1; onCap = false; }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  if (!(tmin >= 0) || tmin > maxDist) return false;

  hit.dist = tmin;
  hit.inside = false;
  hit.exit = 0;
  if (onCap) { hit.nx = 0; hit.ny = capNeg ? -1 : 1; hit.nz = 0; } else {
    const hx = ox + dx * tmin - cx, hz = oz + dz * tmin - cz;
    const len = Math.hypot(hx, hz) || 1;
    hit.nx = hx / len; hit.ny = 0; hit.nz = hz / len;
  }
  return true;
}

// Segment vs oriented rectangle, in 2D — the engine of segmentBlocked(). This
// is where the defect it replaces lived: the old line-of-sight took four POINT
// samples over the whole ray, so at 60 m it tested one point every 15 m and a
// wall two metres from an infected's face was never looked at. A slab test on
// the parameter interval [0, 1] has no sampling in it at all: it either proves
// the segment misses every face of the box or it does not.
function segBox(ax, az, bx, bz, cx, cz, hw, hd, cos, sin) {
  const dx = bx - ax, dz = bz - az;
  const px = ax - cx, pz = az - cz;
  const lox = px * cos + pz * sin;
  const loz = -px * sin + pz * cos;
  const ldx = dx * cos + dz * sin;
  const ldz = -dx * sin + dz * cos;
  let tmin = 0, tmax = 1;                 // starting inside counts as blocked
  if (ldx > -EPS && ldx < EPS) { if (lox < -hw || lox > hw) return false; } else {
    const inv = 1 / ldx;
    let t1 = (-hw - lox) * inv, t2 = (hw - lox) * inv;
    if (t1 > t2) { const s = t1; t1 = t2; t2 = s; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  if (ldz > -EPS && ldz < EPS) { if (loz < -hd || loz > hd) return false; } else {
    const inv = 1 / ldz;
    let t1 = (-hd - loz) * inv, t2 = (hd - loz) * inv;
    if (t1 > t2) { const s = t1; t1 = t2; t2 = s; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  return tmin <= tmax;
}

// Is this point RESTING AGAINST the collider, rather than buried in it? See
// the `openEnds` option on segmentBlocked() for why the question is asked.
//
// Two numbers, and both of them are measured rather than chosen:
//
// REST_TOL covers the contact itself. Every pushOut() in the project
// (buildings.js, props.js, doors.js) moves a body until |local| == the
// collider's half-extent and then stops, so a body resting against a wall sits
// exactly ON the boundary — and segBox's slab interval starts closed at
// tmin = 0, which reads that contact as a block. Net positions arrive
// quantised to 1 cm (main.js `+pos.toFixed(2)`), so half the time the resting
// body is a few millimetres the wrong side of it.
//
// SKIN is the depth limit, and it is what keeps this from becoming "melee
// through anything". The pads that cause the problem are known and small:
// 0.30 m on every props.js collider, 0.35 m on a sealed building footprint,
// 0.15 m on a wall segment's length. A body can only ever be inside one of
// those by the pad, because pushOut() ejects it along the shallowest axis
// every frame. So forgive a contact up to SKIN deep and NOTHING deeper: an
// entity genuinely embedded a metre inside solid geometry (an infected home
// post that landed in a building) stays occluded, which is the behaviour the
// whole service exists to provide.
const REST_TOL = 0.02;
const SKIN = 0.45;

function restingInBox(px, pz, cx, cz, hw, hd, cos, sin) {
  const ax = px - cx, az = pz - cz;
  const lx = ax * cos + az * sin;
  const lz = -ax * sin + az * cos;
  const dx = hw - Math.abs(lx), dz = hd - Math.abs(lz);
  if (dx < -REST_TOL || dz < -REST_TOL) return false;     // outside it entirely
  return (dx < dz ? dx : dz) <= SKIN;                      // inside, but only just
}

function restingInCircle(px, pz, cx, cz, r) {
  const dx = px - cx, dz = pz - cz;
  const d = Math.hypot(dx, dz);
  return d >= r - SKIN && d < r + REST_TOL;
}

// Segment vs circle, in 2D: closest approach of the segment to the centre.
function segCircle(ax, az, bx, bz, cx, cz, r) {
  const dx = bx - ax, dz = bz - az;
  const l2 = dx * dx + dz * dz;
  let t = l2 > EPS ? ((cx - ax) * dx + (cz - az) * dz) / l2 : 0;
  if (t < 0) t = 0; else if (t > 1) t = 1;
  const qx = ax + dx * t - cx, qz = az + dz * t - cz;
  return qx * qx + qz * qz <= r * r;
}

// ---------------------------------------------------------------------------

function finite(n) { return typeof n === 'number' && Number.isFinite(n); }

// Normalise one source entry into { near, surface, kind, maxExtent }.
//
// maxExtent is the largest distance from a collider's CENTRE to its far corner
// in that source. It is load-bearing, not decoration: the corridor is gathered
// in chunks, and a collider is only guaranteed to be offered for a chunk if the
// lookup radius covers half the chunk PLUS this reach. buildings.js registers a
// whole sealed building under its CENTRE cell alone — a 68 m khrushchyovka is
// one OBB with a ~35 m circumradius — so understating this is how a bullet
// would start passing through the near wall of a big building again.
// `shrink` is metres taken off BOTH half-extents of every box in the source
// before it is intersected. It exists because some of the hashes this service
// reads store a MOVEMENT skin rather than a surface: props.js addCol() stores
// every prop at (hw + 0.3, hd + 0.3) so that pushOut() keeps a body's radius
// clear of it. Fed in raw, a 0.12 m fence slab is a 0.80 m wall, a 0.30 m lamp
// post is a 0.90 m box, a round passing 0.44 m beside the post is "blocked",
// the impact decal floats 30 cm off every surface, and a muzzle resting in
// that shell reports the round stopping at distance zero. Shrinking the source
// back to its true extents is the only place a pad can be undone without
// touching the movement contract that put it there.
function source(near, shape, kind, surface, maxExtent) {
  if (!near) return null;
  if (typeof near === 'function') return { near, shape, kind, surface, maxExtent, shrink: 0 };
  return {
    near: near.near,
    shape: near.shape || shape,                  // which intersection to run
    kind: near.kind || kind,                     // what the hit reports itself as
    surface: near.surface || surface,
    // maxExtent may be a FUNCTION, for a hash that is still growing. The
    // browser's buildings.hash is filled as chunks stream in, so a number
    // snapshotted at boot is a number measured over an empty map; main.js
    // passes a closure over buildings.maxObbExtent instead and the latent
    // "a bigger archetype lands and bullets start crossing its facade" bug
    // cannot happen. Read once per lookup — two of those per ray.
    maxExtentFn: typeof near.maxExtent === 'function' ? near.maxExtent : null,
    maxExtent: finite(near.maxExtent) ? near.maxExtent : maxExtent,
    shrink: finite(near.shrink) && near.shrink > 0 ? near.shrink : 0,
  };
}

/**
 * Build a query service over a set of collider providers.
 *
 * Every provider is optional; omit one and that collider class simply never
 * registers a hit, which is what lets the server run with towns-only geometry
 * and a test run with two boxes.
 *
 * provider = {
 *   // --- terrain ---------------------------------------------------------
 *   heightAt(x, z) -> metres            // terrain.getHeight. Omit: no ground.
 *   groundSurfaceAt(x, z) -> tag        // main.js world.surfaceAt. Omit: 'dirt'.
 *   maxHeight, minHeight                // hard bounds on the field (metres).
 *                                       // maxHeight is what makes a shot at
 *                                       // the sky cost nothing — pass it.
 *   cellSize                            // terrain grid cell (default 2.44140625)
 *   terrainStep                         // march spacing (default cellSize / 2)
 *   slopeBound                          // true max |dh/dxz| (default 48)
 *
 *   // --- colliders: a function near(x, z, r), or { near, surface, maxExtent }
 *   obbsNear      // buildings.hash  tuples  -> 'concrete', extent 42
 *   propsNear     // props.colHash   tuples  -> 'metal',    extent 8
 *   doorsNear     // doors.hash      objects -> from door.type
 *   trunksNear    // vegetation.trunkHash    -> 'wood',     extent 2
 *   bodiesNear    // players/infected/animals-> 'flesh',    extent 2
 *   sources: []   // extra sources: { near, shape, kind, surface, maxExtent }
 *                 // shape is 'box' | 'door' | 'trunk' | 'body' (how to
 *                 // intersect it); kind is what a hit reports itself as
 *
 *   worldHalf      // default 5000; the terrain march is clipped to the map
 *   chunk          // corridor chunk length in metres (default 48)
 * }
 */
export function createQuery(provider = {}) {
  const p = provider;
  const heightAt = typeof p.heightAt === 'function' ? p.heightAt : null;
  const groundSurfaceAt = typeof p.groundSurfaceAt === 'function' ? p.groundSurfaceAt : null;
  const maxHeight = finite(p.maxHeight) ? p.maxHeight : Infinity;
  const minHeight = finite(p.minHeight) ? p.minHeight : -Infinity;
  const cellSize = finite(p.cellSize) && p.cellSize > 0 ? p.cellSize : DEFAULT_CELL;
  // Half a terrain cell. See the non-tunnelling argument in marchTerrain().
  const terrainStep = finite(p.terrainStep) && p.terrainStep > 0 ? p.terrainStep : cellSize / 2;
  const slopeBound = finite(p.slopeBound) && p.slopeBound > 0 ? p.slopeBound : DEFAULT_SLOPE_BOUND;
  const coarseStep = finite(p.coarseStep) && p.coarseStep > 0 ? p.coarseStep : 32;
  const worldHalf = finite(p.worldHalf) ? p.worldHalf : 5000;
  const chunk = finite(p.chunk) && p.chunk > 0 ? p.chunk : 48;
  const normalDelta = finite(p.normalDelta) && p.normalDelta > 0 ? p.normalDelta : 1;
  const tol = 0.01;                        // bisection tolerance, 1 cm

  const sources = [
    source(p.obbsNear, 'box', 'obb', 'concrete', 42),
    source(p.propsNear, 'box', 'prop', 'metal', 8),
    source(p.doorsNear, 'door', 'door', null, 2),
    source(p.trunksNear, 'trunk', 'trunk', 'wood', 2),
    source(p.bodiesNear, 'body', 'body', 'flesh', 2),
    ...(Array.isArray(p.sources) ? p.sources.map((s) => source(s, 'box', 'obb', 'concrete', 8)) : []),
  ].filter((s) => s && typeof s.near === 'function');

  // A source's reach, re-read every lookup when the provider gave a function.
  function extentOf(s) {
    if (!s.maxExtentFn) return s.maxExtent;
    const v = s.maxExtentFn();
    return finite(v) ? v : s.maxExtent;
  }

  const stats = { rays: 0, segments: 0, heightSamples: 0, shapeTests: 0, lookups: 0 };

  // scratch — a ray and a segment both run to completion before the next call,
  // so one set of buffers keeps the hot path allocation-free
  const cand = { dist: 0, nx: 0, ny: 0, nz: 0, inside: false, exit: 0 };
  const best = {
    dist: 0, nx: 0, ny: 0, nz: 0, inside: false, exit: 0,
    surface: 'concrete', kind: 'obb', entity: null, zone: null,
  };

  function h(x, z) { stats.heightSamples++; return heightAt(x, z); }

  // -------------------------------------------------------------------------
  // LOCAL SLOPE BOUND, and why the march cannot use the global one.
  //
  // slopeBound is the worst gradient ANYWHERE on the map (47.19, one alpine
  // cliff). Used as the march's Lipschitz constant it makes the provably-safe
  // step f/closing about f/48 — two centimetres for a rifle fired from eye
  // height over flat ground — so the step bound was being overridden by a
  // half-cell FLOOR, which threw the proof away: measured against a 1 cm
  // oracle on the real field, 0.09 % of grazing rays returned null where the
  // ground is genuinely hit and 0.38 % reported the hit up to 160 m late,
  // because f(t) is piecewise quadratic along a line and dips a few cm
  // negative between two half-cell samples.
  //
  // The fix is a bound that is true HERE rather than anywhere: the field is a
  // bilinear interpolation of a cellSize grid, and inside one cell
  //     |dh/dx| <= max(|h10-h00|, |h11-h01|) / cellSize
  //     |dh/dz| <= max(|h01-h00|, |h11-h10|) / cellSize
  // exactly (bilinear du is linear in v and constant in u, so an edge
  // difference IS the derivative at that edge). 94 % of this map's cells are
  // under gradient 1.0, so the safe step stays metres long on open ground and
  // only collapses at a cliff — where collapsing is the point.
  //
  // The bound holds only inside the cell it was read from, so the step is also
  // clipped to the cell exit. Four corner samples per cell, cached, because a
  // march normally takes several steps inside one cell.
  let bcx = NaN, bcz = NaN, bGx = 0, bGz = 0;
  function cellBound(x, z) {
    const ix = Math.floor(x / cellSize), iz = Math.floor(z / cellSize);
    if (ix === bcx && iz === bcz) return;
    bcx = ix; bcz = iz;
    const x0 = ix * cellSize, z0 = iz * cellSize;
    const x1 = x0 + cellSize, z1 = z0 + cellSize;
    const h00 = h(x0, z0), h10 = h(x1, z0), h01 = h(x0, z1), h11 = h(x1, z1);
    bGx = Math.max(Math.abs(h10 - h00), Math.abs(h11 - h01)) / cellSize;
    bGz = Math.max(Math.abs(h01 - h00), Math.abs(h11 - h10)) / cellSize;
  }
  // Forget it. The cache is per RAY, not per service: a provider whose height
  // field can move (a rising tide, a streamed-in finer LOD) must never see a
  // bound read before it changed.
  function resetCellBound() { bcx = NaN; bcz = NaN; }

  // Distance along the ray to the far side of the cellSize cell the point is
  // in — how far the local bound above may be trusted.
  function cellExit(px, pz, dx, dz) {
    let ex = Infinity;
    if (dx > EPS) ex = ((Math.floor(px / cellSize) + 1) * cellSize - px) / dx;
    else if (dx < -EPS) ex = (Math.floor(px / cellSize) * cellSize - px) / dx;
    if (dz > EPS) {
      const e = ((Math.floor(pz / cellSize) + 1) * cellSize - pz) / dz;
      if (e < ex) ex = e;
    } else if (dz < -EPS) {
      const e = (Math.floor(pz / cellSize) * cellSize - pz) / dz;
      if (e < ex) ex = e;
    }
    return ex;
  }

  // -------------------------------------------------------------------------
  // TERRAIN MARCH.
  //
  // Finds the first t in [t0, t1] where the ray meets the height field, i.e.
  // the first root of f(t) = rayY(t) - heightAt(x(t), z(t)).
  //
  // WHY IT CANNOT TUNNEL. Three separate guarantees, in order of how much work
  // they save:
  //
  //  (a) The maxHeight ceiling is EXACT. The field is nowhere above it, so the
  //      stretch of ray above it needs no samples at all — it is clipped
  //      arithmetically before the loop starts. A shot at the sky, or a shot
  //      fired from a 900 m ridge over a valley, therefore costs ~nothing.
  //      This is where the coarse end of the coarse-to-fine actually comes
  //      from, and it cannot be wrong because it is a bound, not a sample.
  //
  //  (b) The step is never longer than the slope bound allows. With clearance
  //      f > 0 at t, the ground can close on the ray no faster than
  //      |dirX| * Gx + |dirZ| * Gz - dirY per metre of t, where Gx / Gz bound
  //      the field's gradient over the cell the ray is in (cellBound above),
  //      so the ray provably cannot be reached before
  //          tSafe = f / (|dirX| * Gx + |dirZ| * Gz - dirY).
  //      Stepping by at most tSafe cannot skip a crossing. The GLOBAL
  //      slopeBound is still used, once, for the early-out: a ray that climbs
  //      faster than the steepest terrain on the whole map can never be
  //      reached by any ground at all, so the march stops outright.
  //
  //  (c) On top of that the step is capped at the cell exit — the local bound
  //      is only true inside the cell it was read from — and at terrainStep,
  //      half a terrain grid cell (1.22 m on this map). The half-cell cap is
  //      the belt to (b)'s braces: it is what a provider whose field is NOT
  //      bilinear on this grid falls back on, and it is never a FLOOR. It used
  //      to be one, which overrode (b) for every nearly-horizontal ray (the
  //      floor was active below 58 m of clearance, i.e. always) and let the
  //      march both miss hits and overshoot them by up to 160 m. A floor can
  //      only ever lengthen a step past a bound that was proven safe, so there
  //      is no floor here, only a 1 cm minimum to guarantee termination.
  //
  // The sign change is then bisected to 1 cm, which is below the width of the
  // impact decal it feeds.
  function marchTerrain(ox, oy, oz, dx, dy, dz, t0, t1, limit) {
    if (!heightAt) return -1;
    const horizRate = Math.hypot(dx, dz);
    let a = t0, b = Math.min(t1, limit);

    // (a) clip to the band that can contain ground
    if (maxHeight < Infinity) {
      if (dy > EPS) {
        const tCeil = (maxHeight - oy) / dy;     // above the ceiling after this
        if (tCeil < b) b = tCeil;
      } else if (dy < -EPS) {
        const tCeil = (maxHeight - oy) / dy;     // below the ceiling after this
        if (tCeil > a) a = tCeil;
      } else if (oy > maxHeight) return -1;
    }
    // Clip to the map. heightAt() answers 0 outside the world, which would read
    // as a sea-level floor stretching to infinity; outside the map there is no
    // terrain at all.
    if (worldHalf < Infinity) {
      if (dx > EPS || dx < -EPS) {
        const inv = 1 / dx;
        let s1 = (-worldHalf - ox) * inv, s2 = (worldHalf - ox) * inv;
        if (s1 > s2) { const s = s1; s1 = s2; s2 = s; }
        if (s1 > a) a = s1;
        if (s2 < b) b = s2;
      } else if (ox < -worldHalf || ox > worldHalf) return -1;
      if (dz > EPS || dz < -EPS) {
        const inv = 1 / dz;
        let s1 = (-worldHalf - oz) * inv, s2 = (worldHalf - oz) * inv;
        if (s1 > s2) { const s = s1; s1 = s2; s2 = s; }
        if (s1 > a) a = s1;
        if (s2 < b) b = s2;
      } else if (oz < -worldHalf || oz > worldHalf) return -1;
    }
    if (a < t0) a = t0;
    if (!(b >= a)) return -1;            // clipped to nothing (or a NaN crept in)

    let tPrev = a;
    let fPrev = oy + dy * a - h(ox + dx * a, oz + dz * a);
    // Already at or under the surface at the start of the band: the origin is
    // inside the ground (a muzzle clipped through a slope, an AI eye below the
    // field). Report it rather than marching out the far side.
    if (fPrev <= 0) return a;

    // (b) the closing-rate early-out: a ray that climbs faster than the
    // steepest possible ground ANYWHERE can never be reached. This is the one
    // place the global bound is still the right tool.
    const gClosing = slopeBound * horizRate - dy;
    if (gClosing <= EPS) return -1;

    const adx = Math.abs(dx), adz = Math.abs(dz);
    let t = a;
    while (t < b) {
      // The GLOBAL bound first, because it costs nothing: with clearance fPrev
      // the ground cannot reach the ray before fPrev / gClosing, anywhere on
      // the map. Where the clearance is large that is already a long step —
      // a round climbing away from the ground, a shot off a ridge — and the
      // local bound cannot beat the half-cell cap anyway, so no samples are
      // spent on it.
      let step = fPrev / gClosing;
      if (step < terrainStep) {
        // The expensive case, and the one the defect lived in: close to the
        // ground, the global bound is a cliff on the far side of the map and
        // collapses the step to centimetres. Four corner samples buy a bound
        // that is true HERE (cellBound) — worth metres of step over the 94 % of
        // cells under gradient 1.0 — and it is trusted only inside its own
        // cell, so the step is clipped to the cell exit as well.
        const px = ox + dx * t, pz = oz + dz * t;
        cellBound(px, pz);
        const closing = adx * bGx + adz * bGz - dy;
        // A non-positive local closing rate means the ground cannot gain on
        // the ray at all inside this cell; the half-cell cap is still applied
        // to it, because that cap is what a provider whose field is NOT
        // bilinear on this grid falls back on and the local bound is only
        // exact for one that is.
        let ls = closing > EPS ? fPrev / closing : terrainStep;
        if (ls > terrainStep) ls = terrainStep;     // (c) half a cell, as a CAP
        // +tol so the next iteration lands in the NEXT cell rather than
        // re-reading this one.
        const ex = cellExit(px, pz, dx, dz) + tol;
        if (ls > ex) ls = ex;
        if (ls > step) step = ls;
      }
      if (step > coarseStep) step = coarseStep;
      if (step < tol) step = tol;                   // termination, not a bound
      t += step;
      if (t > b) t = b;
      const f = oy + dy * t - h(ox + dx * t, oz + dz * t);
      if (f <= 0) {
        // Bracketed: f(tPrev) > 0 >= f(t). Bisect, then finish with ONE SECANT
        // STEP. Bisection alone converges on t, and a 1 cm tolerance in t is up
        // to 13 cm of HEIGHT on this map's steep flanks (and 47 cm at the
        // worst-case gradient), which puts the impact point visibly inside the
        // hillside. f is near-linear over a 1 cm bracket, so interpolating
        // between the two clearances lands on the surface itself.
        let lo = tPrev, hi = t, fLo = fPrev, fHi = f;
        while (hi - lo > tol) {
          const m = (lo + hi) * 0.5;
          const fm = oy + dy * m - h(ox + dx * m, oz + dz * m);
          if (fm > 0) { lo = m; fLo = fm; } else { hi = m; fHi = fm; }
        }
        const span = fLo - fHi;
        return span > EPS ? lo + (hi - lo) * (fLo / span) : hi;
      }
      if (t >= b) break;
      tPrev = t; fPrev = f;
    }
    return -1;
  }

  // Terrain normal by central difference over `normalDelta` (1 m by default —
  // a whole cell, which gives the stable facet normal an impact decal wants
  // rather than the noisy sub-cell slope).
  function terrainNormal(x, z, out) {
    const d = normalDelta;
    const hx = h(x + d, z) - h(x - d, z);
    const hz = h(x, z + d) - h(x, z - d);
    const nx = -hx, ny = 2 * d, nz = -hz;
    const len = Math.hypot(nx, ny, nz) || 1;
    out.nx = nx / len; out.ny = ny / len; out.nz = nz / len;
  }

  // -------------------------------------------------------------------------
  // One chunk of the corridor against one source. Returns the distance of the
  // nearest hit it found, or -1 for none — NOT a reduced limit, because a hit
  // landing exactly on the incoming limit reduces nothing and would be lost.
  function testSource(s, ox, oy, oz, dx, dy, dz, mx, mz, radius, limit) {
    stats.lookups++;
    const list = s.near(mx, mz, radius + extentOf(s));
    if (!list) return -1;
    let lim = limit;
    let hitAt = -1;
    const sh = s.shrink;
    for (let i = 0; i < list.length; i++) {
      const rec = list[i];
      if (!rec) continue;
      stats.shapeTests++;
      let ok = false;
      let surface = s.surface;
      let entity = null;
      let kind = s.kind;
      if (s.shape === 'box') {
        let bx, bz, hw, hd, cos, sin, y0, y1;
        if (Array.isArray(rec)) {
          bx = rec[0]; bz = rec[1]; hw = rec[2]; hd = rec[3];
          cos = rec[4]; sin = rec[5];
          y0 = rec[6] === undefined ? -Infinity : rec[6];
          y1 = rec[7] === undefined ? Infinity : rec[7];
        } else {
          bx = rec.x; bz = rec.z; hw = rec.hw; hd = rec.hd;
          if (finite(rec.yaw)) { cos = Math.cos(rec.yaw); sin = Math.sin(rec.yaw); } else { cos = rec.cos === undefined ? 1 : rec.cos; sin = rec.sin === undefined ? 0 : rec.sin; }
          y0 = finite(rec.y0) ? rec.y0 : -Infinity;
          y1 = finite(rec.y1) ? rec.y1 : Infinity;
          if (rec.surface) surface = rec.surface;
          if (rec.entity !== undefined) entity = rec.entity;
        }
        if (sh) { hw = hw - sh > MIN_HALF ? hw - sh : MIN_HALF; hd = hd - sh > MIN_HALF ? hd - sh : MIN_HALF; }
        ok = rayBox(ox, oy, oz, dx, dy, dz, lim, bx, bz, hw, hd, cos, sin, y0, y1, cand);
      } else if (s.shape === 'door') {
        if (!doorSolid(rec)) continue;
        const y0 = rec.floorY, y1 = rec.floorY + rec.height;
        ok = rayBox(ox, oy, oz, dx, dy, dz, lim,
          rec.cx, rec.cz, rec.half, DOOR_HALF_T, rec.tx, rec.tz, y0, y1, cand);
        if (ok) surface = rec.surface || (rec.type === 'metal' ? 'metal' : 'wood');
        if (ok && rec.entity !== undefined) entity = rec.entity;
      } else if (s.shape === 'trunk') {
        let tx, tz, r, y0, y1;
        if (Array.isArray(rec)) {
          tx = rec[0]; tz = rec[1]; r = rec[2];
          y0 = rec[3] === undefined ? -Infinity : rec[3];
          y1 = rec[4] === undefined ? Infinity : rec[4];
        } else {
          tx = rec.x; tz = rec.z; r = rec.r;
          y0 = finite(rec.y0) ? rec.y0 : -Infinity;
          y1 = finite(rec.y1) ? rec.y1 : Infinity;
          if (rec.surface) surface = rec.surface;
          if (rec.entity !== undefined) entity = rec.entity;
        }
        ok = rayCylinder(ox, oy, oz, dx, dy, dz, lim, tx, tz, r, y0, y1, cand);
      } else if (s.shape === 'body') {
        const y0 = finite(rec.y0) ? rec.y0 : -Infinity;
        const y1 = finite(rec.y1) ? rec.y1 : Infinity;
        ok = rayCylinder(ox, oy, oz, dx, dy, dz, lim, rec.x, rec.z, rec.r, y0, y1, cand);
        if (ok) {
          entity = rec.entity === undefined ? rec : rec.entity;
          if (rec.surface) surface = rec.surface;
        }
      }
      if (!ok || cand.dist > lim) continue;
      lim = cand.dist;
      hitAt = cand.dist;
      best.dist = cand.dist;
      best.nx = cand.nx; best.ny = cand.ny; best.nz = cand.nz;
      best.inside = cand.inside;
      best.exit = cand.exit;
      best.surface = surface;
      best.kind = kind;
      best.entity = entity;
      // Hit zone, for the body sources only. The bands default to the ones
      // zombies.js already draws its hitboxes at (head ~1.62 m, body ~1.1 m).
      if (s.shape === 'body') {
        const hy = oy + dy * cand.dist;
        const headY0 = finite(rec.headY0) ? rec.headY0 : (finite(rec.y1) ? rec.y1 - 0.3 : Infinity);
        const legsY1 = finite(rec.legsY1) ? rec.legsY1 : (finite(rec.y0) ? rec.y0 + 0.9 : -Infinity);
        best.zone = hy >= headY0 ? 'head' : hy <= legsY1 ? 'legs' : 'body';
      } else best.zone = null;
    }
    return hitAt;
  }

  return {
    stats,

    /**
     * What does this ray hit?
     *
     * @param origin  {x, y, z} in metres (a Babylon Vector3 works)
     * @param dir     {x, y, z}; need not be normalised
     * @param maxDist metres along the ray
     * @returns null, or
     *   { point:{x,y,z}, normal:{x,y,z}, dist, surface, kind, entity, zone, inside }
     *   kind    'terrain' | 'obb' | 'prop' | 'door' | 'trunk' | 'body'
     *   surface a SURFACES tag, for the impact effect and the noise classifier
     *   entity  the caller's own object for a body/prop source, else null
     *   zone    'head' | 'body' | 'legs' for a body hit, else null
     *   inside  true when the origin was already inside the collider; dist is
     *           then 0 and normal faces back along the ray
     *   exit    for an `inside` hit only, metres to the far side of that
     *           collider along the ray (0 otherwise). A caller that knows an
     *           origin inside a collider is an ARTEFACT rather than a buried
     *           muzzle needs somewhere to re-fire from: the movement pads
     *           these hashes carry (props.js +0.3, buildings.js +0.35) put the
     *           muzzle inside the very cover the player is using, and
     *           controller.js walks past a 'prop' with this instead of eating
     *           the round at distance zero
     *
     * Returns null for a ray that hits nothing — never a fabricated hit at
     * maxDist, which is what the old `origin + dir * 80` fallback amounted to.
     * Degenerate input (a zero-length or non-finite direction, a non-finite
     * origin, maxDist <= 0) also returns null: there is no line to trace.
     */
    ray(origin, dir, maxDist) {
      if (!origin || !dir) return null;
      const ox = origin.x, oy = origin.y, oz = origin.z;
      if (!finite(ox) || !finite(oy) || !finite(oz)) return null;
      let dx = dir.x, dy = dir.y, dz = dir.z;
      if (!finite(dx) || !finite(dy) || !finite(dz)) return null;
      const len = Math.hypot(dx, dy, dz);
      if (!(len > EPS)) return null;      // a zero-length direction is not a ray
      if (!finite(maxDist) || maxDist <= 0) return null;
      dx /= len; dy /= len; dz /= len;
      stats.rays++;
      resetCellBound();

      let limit = maxDist;
      let found = false;
      // Walk the corridor in ordered chunks so a near hit abandons the rest of
      // the ray. Terrain marches alongside, with its bracket carried across the
      // chunk seam, so a crossing exactly on a boundary cannot slip through.
      const nChunks = Math.ceil(maxDist / chunk);
      for (let ci = 0; ci < nChunks; ci++) {
        const t0 = ci * chunk;
        const t1 = Math.min(maxDist, t0 + chunk);
        if (t0 >= limit) break;
        const half = (t1 - t0) * 0.5;
        const mt = t0 + half;
        const mx = ox + dx * mt, mz = oz + dz * mt;
        for (let si = 0; si < sources.length; si++) {
          const at = testSource(sources[si], ox, oy, oz, dx, dy, dz, mx, mz, half, limit);
          if (at >= 0) { limit = at; found = true; }
        }
        const tg = marchTerrain(ox, oy, oz, dx, dy, dz, t0, t1, limit);
        if (tg >= 0 && tg <= limit) {
          limit = tg;
          found = true;
          best.dist = tg;
          best.inside = false;
          best.exit = 0;
          best.kind = 'terrain';
          best.entity = null;
          best.zone = null;
          const px = ox + dx * tg, pz = oz + dz * tg;
          terrainNormal(px, pz, best);
          best.surface = groundSurfaceAt ? groundSurfaceAt(px, pz) : 'dirt';
        }
        // Everything still unqueried starts at t1 or later, so a hit at or
        // before t1 is final.
        if (found && limit <= t1) break;
      }
      if (!found) return null;
      return {
        point: { x: ox + dx * best.dist, y: oy + dy * best.dist, z: oz + dz * best.dist },
        normal: { x: best.nx, y: best.ny, z: best.nz },
        dist: best.dist,
        surface: best.surface,
        kind: best.kind,
        entity: best.entity,
        zone: best.zone,
        inside: best.inside,
        exit: best.exit,
      };
    },

    /**
     * Is the line from (ax, az) to (bx, az) blocked for a body occupying the
     * vertical band [y0, y1]?
     *
     * This is line-of-sight, melee occlusion and the infected swing. It honours
     * door open state, and it is a segment-vs-box intersection on the whole
     * parameter interval — there is no sampling in it, so the 4-sample /
     * 15 m-granularity flaw it replaces cannot come back.
     *
     * The band is what stops a swing hitting the floor above and an infected
     * reaching over a counter: a collider whose y interval does not overlap
     * [y0, y1] is skipped, so a body band that passes UNDER a raised obstacle
     * (a bridge deck, a raised walkway, a window lintel) is correctly clear.
     *
     * Non-finite input returns TRUE — blocked. Of the two ways to be wrong,
     * refusing a hit is visible and harmless while allowing one is damage
     * through a wall, which is the bug this service exists to kill.
     *
     * `opts.openEnds` — OPT-IN, default off. Skip any collider that CONTAINS
     * one of the two endpoints, instead of reporting it as a block.
     *
     * Why that option has to exist. segBox's parameter interval starts closed
     * at tmin = 0, so a segment whose end sits on or inside a box is blocked by
     * that box — and every pushOut() in the project parks a body at EXACTLY
     * its collider's half-extent, while props.js stores every prop at
     * (hw + 0.3, hd + 0.3) and buildings.js a sealed footprint at
     * (w/2 + 0.35, d/2 + 0.35) because those extents are MOVEMENT skins for a
     * body radius, not surfaces. So there is a 30-35 cm shell around every car,
     * crate, fence and sealed building inside which a resting body is "inside
     * a collider". For melee that was total immunity: with your back to a wall
     * nothing could swing at you, and cornering someone against a wall stopped
     * your own swings working, because the occlusion segment runs feet-to-feet
     * and one of its ends was always in the shell.
     *
     * With openEnds the rule becomes "a collider you are standing in is not
     * what hides you from me", which is the honest question for two bodies.
     * The residual hole is mutual and small: two bodies pressed against
     * OPPOSITE faces of the same collider can reach each other through it.
     * That is worth paying for melee, and it is why MOVEMENT must never pass
     * this flag — a step from outside a wall to inside it would be allowed.
     * wolves.mjs _embedded() depends on the default closed behaviour too.
     */
    segmentBlocked(ax, az, bx, bz, y0, y1, opts) {
      if (!finite(ax) || !finite(az) || !finite(bx) || !finite(bz)) return true;
      let lo = finite(y0) ? y0 : -Infinity;
      let hi = finite(y1) ? y1 : Infinity;
      if (lo > hi) { const s = lo; lo = hi; hi = s; }
      stats.segments++;
      const open = !!(opts && opts.openEnds);

      const dx = bx - ax, dz = bz - az;
      const len = Math.hypot(dx, dz);
      const nChunks = Math.max(1, Math.ceil(len / chunk));
      for (let ci = 0; ci < nChunks; ci++) {
        const s0 = ci / nChunks, s1 = (ci + 1) / nChunks;
        const half = (len * (s1 - s0)) * 0.5;
        const mx = ax + dx * (s0 + s1) * 0.5;
        const mz = az + dz * (s0 + s1) * 0.5;
        // The chunks bound the LOOKUPS, not the slab tests: every collider a
        // chunk offers is tested against the WHOLE line, deliberately. It was
        // reported as duplicate work, and measured it is the opposite — a
        // collider gathered early that blocks the line late ends the call
        // there instead of after every remaining chunk, and the per-collider
        // cost is one slab test either way. Measured over the real towns.json,
        // 4000 lines of 60 m: whole-line 12.5 us/call against 21.8 us for
        // chunk-local sub-segments when the far end is inside a building, and
        // within noise (21.8 vs 22.8) when it is not, with identical answers
        // in 48 000 comparisons. Chunk-local was tried and reverted.
        for (let si = 0; si < sources.length; si++) {
          const s = sources[si];
          stats.lookups++;
          const list = s.near(mx, mz, half + extentOf(s));
          if (!list) continue;
          const sh = s.shrink;
          for (let i = 0; i < list.length; i++) {
            const rec = list[i];
            if (!rec) continue;
            stats.shapeTests++;
            if (s.shape === 'box') {
              let cx, cz, hw, hd, cos, sin, by0, by1;
              if (Array.isArray(rec)) {
                cx = rec[0]; cz = rec[1]; hw = rec[2]; hd = rec[3];
                cos = rec[4]; sin = rec[5];
                by0 = rec[6] === undefined ? -Infinity : rec[6];
                by1 = rec[7] === undefined ? Infinity : rec[7];
              } else {
                cx = rec.x; cz = rec.z; hw = rec.hw; hd = rec.hd;
                if (finite(rec.yaw)) { cos = Math.cos(rec.yaw); sin = Math.sin(rec.yaw); } else { cos = rec.cos === undefined ? 1 : rec.cos; sin = rec.sin === undefined ? 0 : rec.sin; }
                by0 = finite(rec.y0) ? rec.y0 : -Infinity;
                by1 = finite(rec.y1) ? rec.y1 : Infinity;
              }
              if (sh) { hw = hw - sh > MIN_HALF ? hw - sh : MIN_HALF; hd = hd - sh > MIN_HALF ? hd - sh : MIN_HALF; }
              if (lo > by1 || hi < by0) continue;
              if (open && (restingInBox(ax, az, cx, cz, hw, hd, cos, sin)
                || restingInBox(bx, bz, cx, cz, hw, hd, cos, sin))) continue;
              if (segBox(ax, az, bx, bz, cx, cz, hw, hd, cos, sin)) return true;
            } else if (s.shape === 'door') {
              if (!doorSolid(rec)) continue;
              if (lo > rec.floorY + rec.height || hi < rec.floorY) continue;
              if (open && (restingInBox(ax, az, rec.cx, rec.cz, rec.half, DOOR_HALF_T, rec.tx, rec.tz)
                || restingInBox(bx, bz, rec.cx, rec.cz, rec.half, DOOR_HALF_T, rec.tx, rec.tz))) continue;
              if (segBox(ax, az, bx, bz, rec.cx, rec.cz, rec.half, DOOR_HALF_T, rec.tx, rec.tz)) return true;
            } else if (s.shape === 'trunk') {
              let tx, tz, r, by0, by1;
              if (Array.isArray(rec)) {
                tx = rec[0]; tz = rec[1]; r = rec[2];
                by0 = rec[3] === undefined ? -Infinity : rec[3];
                by1 = rec[4] === undefined ? Infinity : rec[4];
              } else {
                tx = rec.x; tz = rec.z; r = rec.r;
                by0 = finite(rec.y0) ? rec.y0 : -Infinity;
                by1 = finite(rec.y1) ? rec.y1 : Infinity;
              }
              if (lo > by1 || hi < by0) continue;
              if (open && (restingInCircle(ax, az, tx, tz, r) || restingInCircle(bx, bz, tx, tz, r))) continue;
              if (segCircle(ax, az, bx, bz, tx, tz, r)) return true;
            } else if (s.shape === 'body') {
              const by0 = finite(rec.y0) ? rec.y0 : -Infinity;
              const by1 = finite(rec.y1) ? rec.y1 : Infinity;
              if (lo > by1 || hi < by0) continue;
              if (open && (restingInCircle(ax, az, rec.x, rec.z, rec.r) || restingInCircle(bx, bz, rec.x, rec.z, rec.r))) continue;
              if (segCircle(ax, az, bx, bz, rec.x, rec.z, rec.r)) return true;
            }
          }
        }
      }
      return false;
    },
  };
}
